/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useContext, Fragment } from 'react';
import PropTypes from 'prop-types';
import { Heading, Icon, Inline } from 'sarsaparilla';
import uniqueId from 'lodash/uniqueId';
import { LayoutControlContext } from './DetailedAvailabilityPageLayout';

function Requirements({
    hasTypeRequirement,
    hasSizeRequirement,
    hasStartingAreaRequirement,
    customRequirements,
}) {
    const Type = (
        <span>
            a <strong>Permit Type</strong>
        </span>
    );
    const Group = (
        <span>
            a <strong>Group Size</strong>
        </span>
    );
    const StartingArea = (
        <span>
            a <strong>Starting Area</strong>
        </span>
    );
    const requirements = [];

    if (hasTypeRequirement) {
        requirements.push({ key: 1, content: Type });
    }

    if (hasSizeRequirement) {
        requirements.push({ key: 2, content: Group });
    }

    if (hasStartingAreaRequirement) {
        requirements.push({ key: 3, content: StartingArea });
    }

    customRequirements.forEach((text) => {
        requirements.push({
            key: uniqueId(),
            content: (
                <span>
                    a <strong>{text}</strong>
                </span>
            ),
        });
    });

    if (requirements.length > 2) {
        const allButLast = requirements.slice(0, requirements.length - 1);

        return (
            <>
                {allButLast.map((item) => {
                    return <span key={item.key}>{item.content}, </span>;
                })}
                <span> and {requirements[requirements.length - 1].content}</span>
            </>
        );
    }

    if (requirements.length === 2) {
        return (
            <>
                {requirements[0].content} and {requirements[1].content}
            </>
        );
    }

    return requirements[0].content;
}

Requirements.propTypes = {
    hasTypeRequirement: PropTypes.bool,
    hasSizeRequirement: PropTypes.bool,
    hasStartingAreaRequirement: PropTypes.bool,
    customRequirements: PropTypes.array,
};

function InformationRequired({
    hasTypeRequirement = false,
    hasSizeRequirement = false,
    hasStartingAreaRequirement = false,
    customRequirements = [],
}) {
    const { isMobileApp } = useContext(LayoutControlContext);
    const hasARequirement =
        hasTypeRequirement ||
        hasSizeRequirement ||
        hasStartingAreaRequirement ||
        !!customRequirements?.length;
    if (!hasARequirement) return null;

    const WrapElement = isMobileApp ? Inline : Fragment;
    const wrapElementProps = isMobileApp
        ? {
              alignX: 'center',
              className: 'mb-2',
          }
        : {};

    return (
        <div className="permit-information-required">
            <div className="permit-information-required-wrap">
                <WrapElement {...wrapElementProps}>
                    <Icon iconName="info-outline" size={isMobileApp ? 'md' : 'xl'} />
                    <Heading
                        headingLevel={2}
                        appearance="h5"
                        className="permit-information-required-heading"
                    >
                        Information Required
                    </Heading>
                </WrapElement>
                <p>
                    Please select{' '}
                    <Requirements
                        hasTypeRequirement={hasTypeRequirement}
                        hasSizeRequirement={hasSizeRequirement}
                        customRequirements={customRequirements}
                        hasStartingAreaRequirement={hasStartingAreaRequirement}
                    />{' '}
                    to view availability.
                </p>
            </div>
        </div>
    );
}
InformationRequired.propTypes = {
    hasTypeRequirement: PropTypes.bool,
    hasSizeRequirement: PropTypes.bool,
    hasStartingAreaRequirement: PropTypes.bool,
    customRequirements: PropTypes.array,
};

export default InformationRequired;
